import { useTranslation } from 'react-i18next'

import { BuyOrder } from '@/types/common'
import { formatLocalFiatAmount } from '@/config/marketPresentation'
import { getPaymentMethod } from '@/utils/payment'
import DescriptionListItem from '@/components/DescriptionListItem'
import { useImageViewer } from '@/components/ImageViewer'
import SectionHeader from '@/components/SectionHeader'

export function ChildOrderPaymentSection({ data, type }: { data: BuyOrder; type: 'buy' | 'sell' }) {
  const { t } = useTranslation()
  const paymentInfo = data.sellOrderPaymentMethod ?? data.paymentMethod
  const paymentMethod = getPaymentMethod(paymentInfo)
  const { setSource, setType } = useImageViewer()
  const hasRevisingLabel =
    data.status === 8 || (data.status === 9 && data.dealAmount.value !== data.originDealAmount.value)
  const isVirtual = data.buyer === '0'

  if (isVirtual) {
    return null
  }

  return (
    <>
      <SectionHeader>{t(type === 'buy' ? 'orders:sellerReceivingInformation' : 'orders:buyerPaymentInformation')}</SectionHeader>
      <DescriptionListItem label={t('orders:paymentMethod')} hasBorderTop={true}>
        {t(`payment:${paymentMethod}`)}
      </DescriptionListItem>
      {paymentMethod === 'bank' && (
        <>
          <DescriptionListItem label={t('orders:name')} canCopy={true}>
            {paymentInfo[paymentMethod]?.name || ''}
          </DescriptionListItem>
          <DescriptionListItem label={t('orders:bankName')} canCopy={true}>
            {paymentInfo[paymentMethod]?.bankName || ''}
          </DescriptionListItem>
          <DescriptionListItem label={t('orders:bankBranch')} canCopy={true}>
            {paymentInfo[paymentMethod]?.branchName || ''}
          </DescriptionListItem>
          <DescriptionListItem
            label={t('orders:bankAccountNumber')}
            canCopy={true}
            hasBorderBottom={type === 'sell' || ![2, 3, 8, 9].includes(data.status)}
          >
            {paymentInfo[paymentMethod]?.account || ''}
          </DescriptionListItem>
        </>
      )}
      {(paymentMethod === 'alipay' || paymentMethod === 'wechat' || paymentMethod === 'qq') && (
        <>
          <DescriptionListItem label={t('orders:name')} canCopy={true}>
            {paymentInfo[paymentMethod]?.name || ''}
          </DescriptionListItem>
          <DescriptionListItem label={t('orders:account', { method: t(`payment:${paymentMethod}`) })} canCopy={true}>
            {paymentInfo[paymentMethod]?.account || ''}
          </DescriptionListItem>
          <DescriptionListItem
            label={t('orders:receivingCode', { method: t(`payment:${paymentMethod}`) })}
            hasBorderBottom={type === 'sell' || ![2, 3, 8, 9].includes(data.status)}
            onOpenQrCode={() => {
              setType('qrcode')
              setSource(paymentInfo[paymentMethod]?.url || '')
            }}
          >
            {''}
          </DescriptionListItem>
        </>
      )}
      {paymentMethod === 'ermb' && (
        <>
          <DescriptionListItem label={t('orders:name')} canCopy={true}>
            {paymentInfo[paymentMethod]?.name || ''}
          </DescriptionListItem>
          <DescriptionListItem
            label={t('orders:account', { method: t(`payment:${paymentMethod}`) })}
            canCopy={true}
            hasBorderBottom={type === 'sell' || ![2, 3, 8, 9].includes(data.status)}
          >
            {paymentInfo[paymentMethod]?.account || ''}
          </DescriptionListItem>
        </>
      )}
      {type === 'buy' && [2, 3, 8, 9].includes(data.status) && (
        <DescriptionListItem
          label={t('orders:paymentAmount')}
          isValuePrimary={true}
          canCopy={true}
          description={hasRevisingLabel ? t('orders:revising') : undefined}
        >
          {formatLocalFiatAmount(hasRevisingLabel ? data.originDealAmount.value : data.dealAmount.value)}
        </DescriptionListItem>
      )}
    </>
  )
}
